import React, { useState } from 'react'
import {
  PlusIcon,
  UserIcon,
  FileTextIcon,
  CalendarIcon,
  GraduationCapIcon,
  SchoolIcon,
  TrashIcon,
  AlertCircleIcon,
  XIcon,
  SearchIcon,
} from 'lucide-react'
import { Student } from '../types'
import { formatDate, getStudentFullName } from '../utils/helpers'
import { deleteStudent } from '../utils/api'

interface StudentListProps {
  students: Student[]
  error?: string | null
  onSelectStudent: (student: Student) => void
  onCreateNew: () => void
  onStudentUpdated?: () => void
  onRetry?: () => void
}

export const StudentList = ({
  students,
  error,
  onSelectStudent,
  onCreateNew,
  onStudentUpdated,
  onRetry,
}: StudentListProps) => {
  const [deletingStudents, setDeletingStudents] = useState<Set<string>>(new Set())
  const [showErrorBanner, setShowErrorBanner] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  const handleDeleteStudent = async (student: Student, event: React.MouseEvent) => {
    event.stopPropagation() // Prevent triggering onSelectStudent
    
    const confirmMessage = `Are you sure you want to delete "${getStudentFullName(student)}"?\n\nThis will:\n• Mark the student as inactive\n• Keep all documents linked\n• Allow restoration later\n\nThis action can be undone.`
    
    if (!window.confirm(confirmMessage)) {
      return
    }
    
    try {
      setDeletingStudents(prev => new Set(prev).add(student.id))
      await deleteStudent(student.id)
      
      // Refresh the student list to remove deleted student
      if (onStudentUpdated) {
        onStudentUpdated()
      }
    } catch (error: any) {
      console.error('Failed to delete student:', error)
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete student. Please try again.'
      alert(`Error: ${errorMessage}`)
    } finally {
      setDeletingStudents(prev => {
        const updated = new Set(prev)
        updated.delete(student.id)
        return updated
      })
    }
  }

  // Filter students based on search query
  const filteredStudents = students.filter(student => {
    const fullName = getStudentFullName(student).toLowerCase()
    const query = searchQuery.toLowerCase()
    return fullName.includes(query) ||
      student.first_name.toLowerCase().includes(query) ||
      student.last_name.toLowerCase().includes(query) ||
      (student.school_name && student.school_name.toLowerCase().includes(query)) ||
      (student.grade_level && student.grade_level.toLowerCase().includes(query)) ||
      (student.student_id_number && student.student_id_number.toLowerCase().includes(query))
  })

  const getStatusIcon = (student: Student) => {
    if (student.is_active === 0) {
      return <AlertCircleIcon className="h-4 w-4 text-red-500" />
    }
    return <UserIcon className="h-4 w-4 text-green-500" />
  }

  const getStatusText = (student: Student) => {
    if (student.is_active === 0) {
      return 'Inactive'
    }
    return 'Active'
  }

  const getDocumentCountText = (count?: number) => {
    if (!count) return 'No documents'
    if (count === 1) return '1 document'
    return `${count} documents`
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Your Students</h1>
        <button
          onClick={onCreateNew}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add New Student
        </button>
      </div>

      {/* Error Banner */}
      {error && showErrorBanner && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error Loading Students</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded hover:bg-red-200"
                >
                  Retry
                </button>
              )}
              <button
                onClick={() => setShowErrorBanner(false)}
                className="text-red-500 hover:text-red-700"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <SearchIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search students by name, school, grade, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Student List */}
      {filteredStudents.length === 0 ? (
        <div className="text-center py-12">
          <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          {searchQuery ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No students found</h3>
              <p className="text-gray-500 mb-4">
                No students match your search query "{searchQuery}".
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="text-blue-600 hover:text-blue-700"
              >
                Clear search
              </button>
            </>
          ) : students.length === 0 ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No students yet</h3>
              <p className="text-gray-500 mb-4">
                Get started by adding your first student to organize their documents.
              </p>
              <button
                onClick={onCreateNew}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Your First Student
              </button>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No active students</h3>
              <p className="text-gray-500">All students have been deactivated.</p>
            </>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredStudents.map((student) => (
            <div
              key={student.id}
              onClick={() => onSelectStudent(student)}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:border-blue-300 cursor-pointer transition-all duration-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Student Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      {getStatusIcon(student)}
                      <h3 className="text-lg font-semibold text-gray-900 ml-2">
                        {getStudentFullName(student)}
                      </h3>
                      <span className={`ml-3 px-2 py-1 text-xs rounded-full ${
                        student.is_active === 0 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {getStatusText(student)}
                      </span>
                    </div>
                  </div>

                  {/* Student Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {/* Basic Info */}
                    <div className="space-y-2">
                      {student.date_of_birth && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <span>Born {formatDate(student.date_of_birth)}</span>
                        </div>
                      )}
                      {student.grade_level && (
                        <div className="flex items-center text-sm text-gray-600">
                          <GraduationCapIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <span>Grade {student.grade_level}</span>
                        </div>
                      )}
                    </div>

                    {/* School Info */}
                    <div className="space-y-2">
                      {student.school_name && (
                        <div className="flex items-center text-sm text-gray-600">
                          <SchoolIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <span>{student.school_name}</span>
                        </div>
                      )}
                      {student.student_id_number && (
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="text-gray-400 mr-2">ID:</span>
                          <span>{student.student_id_number}</span>
                        </div>
                      )}
                    </div>

                    {/* Document Info */}
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <FileTextIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{getDocumentCountText(student.document_count)}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Added {formatDate(student.created_at)}
                      </div>
                    </div>
                  </div>

                  {/* Notes Preview */}
                  {student.notes && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-md">
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {student.notes}
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="ml-4 flex items-center space-x-2">
                  <button
                    onClick={(e) => handleDeleteStudent(student, e)}
                    disabled={deletingStudents.has(student.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete student"
                  >
                    {deletingStudents.has(student.id) ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results Count */}
      {filteredStudents.length > 0 && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Showing {filteredStudents.length} of {students.length} students
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        </div>
      )}
    </div>
  )
}